/**
 * Recycle-kiosk WS contract — single source of truth for the agent↔player
 * protocol. The Python agent (Plan A) validates its messages against the
 * generated JSON-Schema golden (recycle-contract.schema.json).
 *
 * Envelope (both directions): { v: 1, type, txnId, ts, data }.
 *
 * Ground truth for every shape below is the built Python agent at
 * infra/device/recycle-agent/src/dooh_recycle_agent/{ws_server,orchestrator,
 * agent,state}.py and tests/test_integration_full_flow.py — NOT the initial
 * task-B1-brief.md sketch. See the "deviations from the brief" note in
 * task-B1-report.md for the list of places this file intentionally diverges
 * from that sketch.
 */
import { z } from 'zod';
// ─── Constants ────────────────────────────────────────────────────────────────
export const RECYCLE_AGENT_WS_URL = 'ws://127.0.0.1:8765';
export const RECYCLE_PRODUCT_DISPLAY_MS = 5000;
export const RECYCLE_RECONNECT_MIN_MS = 1000;
export const RECYCLE_RECONNECT_MAX_MS = 30000;
/** Reserved manifest playlist keys for recycle screen media (CMS-themable). */
export const RECYCLE_PLAYLIST_KEYS = {
    processing: 'recycle:processing',
    deposit_item: 'recycle:deposit_item',
    no_match: 'recycle:no_match',
    barcode_not_qr: 'recycle:barcode_not_qr',
    no_item_detected: 'recycle:no_item_detected',
    product: 'recycle:product',
};
export const RECYCLE_EVENT_TYPES = [
    'agent_status', 'scan', 'awaiting_verdict', 'verdict', 'deposit_started',
    'result', 'no_match', 'qr_reject', 'obstructed', 'restart_required',
    'reward', 'verdict_timeout', 'recovery_complete', 'scan_rejected',
];
export const RECYCLE_COMMAND_TYPES = [
    'ack_screen_done', 'operator_clear_attempt', 'simulate_scan',
];
// ─── Health ───────────────────────────────────────────────────────────────────
// Ground truth (hardware/door.py, hardware/drop_sensor.py, hardware/barcode.py,
// reward.py, bin_telemetry.py `.health()`): every component health probe
// returns exactly "ok" | "absent" | "error". "degraded" does not exist
// anywhere in the agent.
export const healthComponentSchema = z.enum(['ok', 'absent', 'error']);
// agent.py `_health_snapshot()` / `publish_status()` broadcasts
// { state, servo, dropSensor, barcode, nfc, binFillPercent } on the periodic
// health tick and on MQTT connection-state changes, but orchestrator.py also
// broadcasts bare { state } agent_status events on go-idle / recovery-success
// transitions, and ws_server.py's default snapshot before any status has
// been published is just { state: "IDLE" }. So only nothing is unconditionally
// required except when present each field must satisfy its type — all
// fields are therefore optional. binFillPercent can be null before the bin
// telemetry monitor's first reading (`BinTelemetry.latest_fill_percent()`
// starts as None).
export const agentStatusHealthSchema = z.object({
    state: z.string().optional(),
    servo: healthComponentSchema.optional(),
    dropSensor: healthComponentSchema.optional(),
    barcode: healthComponentSchema.optional(),
    nfc: healthComponentSchema.optional(),
    binFillPercent: z.number().min(0).max(100).nullable().optional(),
    // v2 additive: present only while state == "OUT_OF_SERVICE". The agent omits
    // it entirely on a clear transition, so it stays optional. The player overlay
    // uses it to render bin_full vs hardware_fault copy.
    outOfServiceReason: z.enum(['bin_full', 'hardware_fault']).optional(),
});
// ─── Envelope helper ────────────────────────────────────────────────────────────
// txnId is nullable: ws_server.build_envelope's txn_id param is `str | None`,
// and the very first agent_status snapshot a client receives on connect is
// built with txn_id=None (see WsServer._handler's default `_last_status`).
const envelope = (type, data) => z.object({
    v: z.literal(1),
    type: z.literal(type),
    txnId: z.string().nullable(),
    ts: z.string(),
    data,
});
// ─── Event data schemas ─────────────────────────────────────────────────────────
// scan always carries isQr alongside barcode (orchestrator.on_scan emits
// {"barcode": scan.barcode, "isQr": scan.is_qr} — confirmed by
// test_integration_full_flow.py: events[0]["data"] == {"barcode": ..., "isQr": False}).
const scanData = z.object({ barcode: z.string(), isQr: z.boolean() });
// awaiting_verdict only ever carries barcode (orchestrator.on_scan:
// await self._emit("awaiting_verdict", txn_id, {"barcode": scan.barcode})).
const awaitingVerdictData = z.object({ barcode: z.string() });
const verdictData = z.object({
    recyclable: z.boolean(),
    // No .transform() here — z.toJSONSchema() (golden generation) rejects
    // transforms. .default(null) makes the field optional on input and yields
    // null when omitted, which the reducer stores directly.
    playlistOverride: z.string().nullable().default(null),
    // v2 additive: DOOH-owned reward. Transaction-specific QR when the CMS
    // created a CC Rewards pending claim online; null when the verdict came from
    // the offline cached catalog (player renders a generic QR instead). Omitted
    // entirely by the legacy agent build, so it defaults to null.
    rewardQrUrl: z.string().nullable().default(null),
    // v2 additive: product display block for the success screen. Optional so the
    // offline accept path (no product metadata) still validates.
    product: z.object({
        name: z.string(),
        brand: z.string().optional(),
        category: z.string().optional(),
        imageUrl: z.string().optional(),
    }).optional(),
});
// Agent authority (orchestrator.on_verdict): result carries
// {"outcome": "success" | "no_item"}, never {"success": boolean} as the
// brief's original sketch had it.
const resultData = z.object({ outcome: z.enum(['success', 'no_item']) });
// Agent authority (state.py Transaction.clear_attempts starts at 0; the
// first obstruction of a transaction is emitted with attempt: 0, before any
// escalation has incremented the counter — confirmed by
// test_integration_full_flow.py: events[-1]["data"]["attempt"] == 0).
const obstructedData = z.object({ attempt: z.number().int().min(0) });
const rewardData = z.object({ qrUrl: z.string() });
// v2 additive: fail-closed scan gating (plan Task 1.1). Emitted with
// txnId null — the scan was refused, so no transaction was ever created.
// bin_full: kiosk is OUT_OF_SERVICE because the bin tripped the full
// threshold (D4); hardware_fault: door servo / drop sensor health != ok.
const scanRejectedData = z.object({
    reason: z.enum(['bin_full', 'hardware_fault']),
});
const emptyData = z.object({}).strict();
const simulateScanData = z.object({ barcode: z.string() });
// ─── Events (agent → player) ─────────────────────────────────────────────────────
export const recycleEventSchema = z.discriminatedUnion('type', [
    envelope('agent_status', agentStatusHealthSchema),
    envelope('scan', scanData),
    envelope('awaiting_verdict', awaitingVerdictData),
    envelope('verdict', verdictData),
    envelope('deposit_started', emptyData),
    envelope('result', resultData),
    envelope('no_match', emptyData),
    envelope('qr_reject', emptyData),
    envelope('obstructed', obstructedData),
    envelope('restart_required', emptyData),
    envelope('reward', rewardData),
    envelope('verdict_timeout', emptyData),
    // v2 additive: emitted exactly once after a successful obstruction clear so
    // the player reducer can unstick the OBSTRUCTED overlay and flush the
    // interaction. Replaces the synthetic local-reset workaround.
    envelope('recovery_complete', emptyData),
    envelope('scan_rejected', scanRejectedData),
]);
// ─── Commands (player → agent) ───────────────────────────────────────────────────
export const recycleCommandSchema = z.discriminatedUnion('type', [
    envelope('ack_screen_done', emptyData),
    envelope('operator_clear_attempt', emptyData),
    envelope('simulate_scan', simulateScanData),
]);
//# sourceMappingURL=recycle-contract.js.map